import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Stack, Box, Typography, LinearProgress, CircularProgress, Portal } from '@mui/material';
import fielderLogo from '@assets/icons/fielder-logo.svg';
import PropTypes from 'prop-types';
import { selectLoadingConfig } from '@core/store/modules/loading/loading.selector';
import { FielderHard } from '@core/theme/baseTheme';

const toPx = (v, fallback) =>
  typeof v === 'number' ? v : (typeof v === 'string' && v.endsWith('px') ? parseInt(v, 10) : fallback);
const Loading = (
  {
   size = null,
   thickness = 2,
   inline = false,
   textOverride = '',
   progressOverride = null,
  }) => {
  const reduxConfig = useSelector(selectLoadingConfig) || {};
  const { text, progress } = reduxConfig;
  const pxSizeInline = useMemo(() => toPx(size, 40), [size]);
  const pxSizeFull = useMemo(() => toPx(size, 200), [size]);
  if (inline) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: pxSizeInline,
          height: pxSizeInline,
        }}
      >
        <CircularProgress size={pxSizeInline} thickness={thickness} sx={{ color: FielderHard }} />
        <Box
          component="img"
          src={fielderLogo}
          alt="Fielder logo"
          sx={{ position: 'absolute', width: '50%', height: '50%' }}
        />
      </Box>
    );
  }
  const showProgress =
    Number.isFinite(progressOverride ?? progress) &&
    Math.round(progressOverride ?? progress) < 100;
  return (
    <Portal>
      <Box
        id="fielder-loading"
        role="status"
        aria-live="polite"
        sx={(theme) => ({
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          // Theme-aware overlay:
          bgcolor:
            theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.55)'
              : 'rgba(255,255,255,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: theme.zIndex.modal + 10,
          overflow: 'hidden',
          transition: 'background-color 150ms ease',
        })}
      >
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={6}
          sx={{ width: '100%', height: '100%' }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'relative',
              width: 'min(200px, 60vw)',
              height: pxSizeFull,
              minHeight: 160,
              mt: 2,
            }}
          >
            <CircularProgress
              size={Math.max(pxSizeFull, 160)}
              thickness={thickness}
              sx={{ color: FielderHard }}
            />
            <Box
              component="img"
              src={fielderLogo}
              alt="Fielder logo"
              sx={{ position: 'absolute', width: '50%', height: '50%' }}
            />
          </Box>
          {(textOverride || text) && (
            <Typography
              variant="body1"
              textAlign="center"
              px={2}
              sx={(theme) => ({
                wordBreak: 'break-word',
                color: theme.palette.text.primary,
              })}
            >
              {textOverride || text}
            </Typography>
          )}
          {showProgress && (
            <Box width={{ xs: '80%', sm: '60%', md: '50%', lg: '40%' }} textAlign="center" mt={1}>
              <LinearProgress
                variant="determinate"
                value={Math.round(progressOverride ?? progress)}
                sx={(theme) => ({
                  height: 10,
                  borderRadius: 5,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[300],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.primary.main,
                    animation: 'progress-bar-stripes 0.5s linear infinite',
                    backgroundSize: '1rem 1rem',
                  },
                })}
              />
              <Typography
                variant="body2"
                mt={1}
                sx={(theme) => ({
                  fontFamily: 'Lato',
                  fontSize: '30px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: theme.palette.text.primary,
                })}
              >
                {`${Math.round(progressOverride ?? progress)}%`}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Portal>
  );
};
Loading.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  thickness: PropTypes.number,
  inline: PropTypes.bool,
  textOverride: PropTypes.string,
  progressOverride: PropTypes.number,
};
export default Loading;
